import { useState, useEffect } from "react";
import { Droplets, Zap, Leaf, Factory, Sun, X, Settings, Plus, Minus, Calendar, TrendingUp, Edit3, Save, RotateCcw } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  AreaChart,
  Area,
  ComposedChart,
  Line
} from "recharts";
import { createPortal } from "react-dom";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

// Configuración inicial de bombas por presa
const DEFAULT_BOMBAS = {
  PARRALILLO: { cantidad: 1, potencia_cv: 30, horas_dia: 2 },
  SIBERIO: { cantidad: 1, potencia_cv: 30, horas_dia: 2 },
  CAIDERO: { cantidad: 1, potencia_cv: 30, horas_dia: 2 }
};

// Riego mensual por defecto (m³) - valores estimados
const DEFAULT_RIEGO_MENSUAL = {
  Ene: 180000, Feb: 160000, Mar: 200000, Abr: 240000,
  May: 280000, Jun: 320000, Jul: 360000, Ago: 350000,
  Sep: 290000, Oct: 200000, Nov: 140000, Dic: 80000
};

// Datos de La Aldea de San Nicolás
const RIEGO_BASE = {
  hectareas_regables: 850,
  consumo_bombeo_total_kwh_ha: 617.5, // kWh por hectárea si todo fuera bombeo
  coste_kwh: 0.15, // €/kWh
  kg_co2_kwh: 0.233, // kg CO2 por kWh
  arboles_por_tonelada_co2: 47.6 // árboles para absorber 1 tonelada de CO2/año
};

const MESES = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];

const formatNumber = (num) => {
  if (num >= 1000000) return (num / 1000000).toFixed(2) + "M";
  if (num >= 1000) return (num / 1000).toFixed(1) + "k";
  return num.toLocaleString('es-ES');
};

const IrrigationStats = ({ isOpen, onClose }) => {
  // Configuración de bombas
  const [bombas, setBombas] = useState(() => {
    const saved = localStorage.getItem('centinela_bombas');
    return saved ? JSON.parse(saved) : DEFAULT_BOMBAS;
  });
  
  // Riego mensual manual (m³)
  const [riegoMensual, setRiegoMensual] = useState(() => {
    const saved = localStorage.getItem('centinela_riego_mensual');
    return saved ? JSON.parse(saved) : DEFAULT_RIEGO_MENSUAL;
  });
  
  const [showConfig, setShowConfig] = useState(false);
  const [showRiegoEditor, setShowRiegoEditor] = useState(false);
  const [editingRiego, setEditingRiego] = useState({ ...DEFAULT_RIEGO_MENSUAL });

  // Guardar en localStorage cuando cambian los valores
  useEffect(() => {
    localStorage.setItem('centinela_bombas', JSON.stringify(bombas));
  }, [bombas]);

  useEffect(() => {
    localStorage.setItem('centinela_riego_mensual', JSON.stringify(riegoMensual));
  }, [riegoMensual]);

  // Calcular total anual de riego
  const totalRiegoAnual = Object.values(riegoMensual).reduce((sum, val) => sum + val, 0);
  const totalRiegoAnualHm3 = (totalRiegoAnual / 1000000).toFixed(2);

  // Calcular estadísticas basadas en configuración de bombas y riego mensual
  const calcularEstadisticas = () => {
    // Potencia total instalada
    let potencia_total_cv = 0;
    let potencia_total_kw = 0;
    
    Object.entries(bombas).forEach(([presa, config]) => {
      potencia_total_cv += config.potencia_cv * config.cantidad;
      potencia_total_kw += config.potencia_cv * 0.7355 * config.cantidad;
    });

    // Consumo TEÓRICO si TODO el agua se bombeara (sin gravedad)
    // Usar 0.5 kWh por m³ de agua bombeada (típico para elevación de 50-100m)
    const kwh_por_m3 = 0.5;
    const consumo_bombeo_total = totalRiegoAnual * kwh_por_m3;
    
    // Consumo REAL con las bombas (solo se usan para zonas altas)
    // Se estima que solo el 5-10% del agua necesita bombeo
    const porcentaje_bombeo = 0.05; // 5% del agua necesita bombeo
    const horas_bomba_anuales = Object.values(bombas).reduce((sum, config) => 
      sum + (config.horas_dia * config.cantidad * 200), 0); // 200 días de riego
    const consumo_real_kwh = potencia_total_kw * (horas_bomba_anuales / 3); // Dividido entre las 3 presas
    
    // Ahorro = Lo que costaría bombear todo - Lo que realmente consumimos
    const ahorro_kwh = Math.max(0, consumo_bombeo_total - consumo_real_kwh);
    const ahorro_porcentaje = consumo_bombeo_total > 0 
      ? Math.min(99, ((ahorro_kwh / consumo_bombeo_total) * 100)).toFixed(1) 
      : "0.0";
    
    // CO2 evitado
    const co2_evitado = ahorro_kwh * RIEGO_BASE.kg_co2_kwh;
    
    // Coste evitado
    const coste_evitado = ahorro_kwh * RIEGO_BASE.coste_kwh;

    return {
      consumo_real_kwh,
      consumo_bombeo_total,
      ahorro_kwh,
      ahorro_porcentaje,
      co2_evitado,
      coste_evitado,
      potencia_total_cv,
      potencia_total_kw,
      arboles_equivalentes: Math.round(co2_evitado / 21) // 21 kg CO2/árbol/año
    };
  };

  const stats = calcularEstadisticas();

  // Datos para gráficas basados en riego mensual real
  const datosAhorroMensual = MESES.map((mes) => {
    const riego = riegoMensual[mes] || 0;
    const factor = totalRiegoAnual > 0 ? riego / totalRiegoAnual : 0;
    const bombeo = Math.round(stats.consumo_bombeo_total * factor);
    const gravedad = Math.round(stats.consumo_real_kwh * factor);
    const ahorro = bombeo - gravedad;
    return {
      mes,
      riego_m3: riego,
      riego_miles: Math.round(riego / 1000),
      bombeo_mwh: Math.round(bombeo / 1000),
      gravedad_mwh: Math.round(gravedad / 1000),
      ahorro_mwh: Math.round(ahorro / 1000),
      co2_kg: Math.round(ahorro * RIEGO_BASE.kg_co2_kwh)
    };
  });

  // Datos para gráfica de CO2
  const datosCO2 = MESES.map((mes) => {
    const riego = riegoMensual[mes] || 0;
    const factor = totalRiegoAnual > 0 ? riego / totalRiegoAnual : 0;
    const co2 = Math.round(stats.co2_evitado * factor);
    return {
      mes,
      co2_evitado: co2,
      arboles: Math.round(co2 / 21)
    };
  });

  // Actualizar configuración de bomba
  const actualizarBomba = (presa, campo, valor) => {
    setBombas(prev => ({
      ...prev,
      [presa]: {
        ...prev[presa],
        [campo]: Math.max(0, parseInt(valor) || 0)
      }
    }));
  };

  // Actualizar riego mensual
  const actualizarRiego = (mes, valor) => {
    const numVal = parseInt(valor.replace(/\D/g, '')) || 0;
    setEditingRiego(prev => ({
      ...prev,
      [mes]: numVal
    }));
  };

  // Guardar riego editado
  const guardarRiego = () => {
    setRiegoMensual(editingRiego);
    setShowRiegoEditor(false);
  };

  // Resetear a valores por defecto
  const resetearRiego = () => {
    setEditingRiego({ ...DEFAULT_RIEGO_MENSUAL });
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/90 backdrop-blur-sm" data-testid="irrigation-stats-modal">
      <div className="bg-[#0a0f18] border border-green-900/50 rounded-xl w-[96vw] max-w-7xl max-h-[95vh] overflow-hidden shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-3 border-b border-green-900/30 bg-gradient-to-r from-green-900/40 to-teal-900/30">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-600/30 rounded-lg">
              <Droplets className="w-6 h-6 text-green-400" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-white uppercase tracking-wide">Estadísticas de Riego - La Aldea</h2>
              <p className="text-xs text-zinc-400">
                Sistema de gravedad · {stats.potencia_total_cv} CV total · {totalRiegoAnualHm3} Hm³/año
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setEditingRiego({ ...riegoMensual });
                setShowRiegoEditor(true);
              }}
              className="bg-blue-600/20 text-blue-300 border-blue-700 hover:bg-blue-600/30"
            >
              <Calendar className="w-4 h-4 mr-1" />
              Riego Mensual
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowConfig(!showConfig)}
              className={`${showConfig ? 'bg-green-600 text-white' : 'bg-zinc-800 text-zinc-300'} border-green-700`}
            >
              <Settings className="w-4 h-4 mr-1" />
              Bombas
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
              className="text-zinc-400 hover:text-white hover:bg-zinc-800"
            >
              <X className="w-5 h-5" />
            </Button>
          </div>
        </div>

        {/* Content */}
        <div className="p-4 overflow-y-auto max-h-[calc(95vh-70px)]">
          
          {/* Editor de Riego Mensual */}
          {showRiegoEditor && (
            <Card className="bg-blue-900/20 border-blue-800/50 p-4 mb-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-bold text-blue-300 uppercase flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  Introducir Riego Mensual (m³)
                </h3>
                <div className="flex gap-2">
                  <Button size="sm" variant="ghost" onClick={resetearRiego} className="text-zinc-400 hover:text-white">
                    <RotateCcw className="w-4 h-4 mr-1" />
                    Resetear
                  </Button>
                  <Button size="sm" onClick={guardarRiego} className="bg-blue-600 hover:bg-blue-700 text-white">
                    <Save className="w-4 h-4 mr-1" />
                    Guardar
                  </Button>
                </div>
              </div>
              <div className="grid grid-cols-6 gap-3">
                {MESES.map((mes) => (
                  <div key={mes} className="space-y-1">
                    <Label className="text-[10px] text-zinc-400 uppercase">{mes}</Label>
                    <Input
                      type="text"
                      value={editingRiego[mes]?.toLocaleString('es-ES') || '0'}
                      onChange={(e) => actualizarRiego(mes, e.target.value)}
                      className="bg-zinc-900 border-zinc-700 text-white font-mono text-sm h-8 text-center"
                    />
                  </div>
                ))}
              </div>
              <div className="mt-3 flex items-center justify-between text-sm">
                <span className="text-zinc-400">Total Anual:</span>
                <span className="text-blue-300 font-bold font-mono">
                  {Object.values(editingRiego).reduce((a, b) => a + b, 0).toLocaleString('es-ES')} m³ 
                  ({(Object.values(editingRiego).reduce((a, b) => a + b, 0) / 1000000).toFixed(2)} Hm³)
                </span>
              </div>
            </Card>
          )}
          
          {/* Panel de Configuración de Bombas */}
          {showConfig && (
            <Card className="bg-zinc-900/80 border-green-800/50 p-4 mb-4">
              <h3 className="text-sm font-bold text-green-300 uppercase mb-3 flex items-center gap-2">
                <Factory className="w-4 h-4" />
                Configuración de Bombas por Presa
              </h3>
              <div className="grid grid-cols-3 gap-4">
                {Object.entries(bombas).map(([presa, config]) => (
                  <div key={presa} className="bg-zinc-800/50 rounded-lg p-3 border border-zinc-700">
                    <h4 className="text-sm font-bold text-white mb-3 text-center">{presa}</h4>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-zinc-400">Nº Bombas:</span>
                        <div className="flex items-center gap-1">
                          <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => actualizarBomba(presa, 'cantidad', config.cantidad - 1)}>
                            <Minus className="w-3 h-3" />
                          </Button>
                          <span className="w-8 text-center text-white font-bold">{config.cantidad}</span>
                          <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => actualizarBomba(presa, 'cantidad', config.cantidad + 1)}>
                            <Plus className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-zinc-400">Potencia (CV):</span>
                        <Input
                          type="number"
                          value={config.potencia_cv}
                          onChange={(e) => actualizarBomba(presa, 'potencia_cv', e.target.value)}
                          className="w-20 h-7 text-center bg-zinc-900 border-zinc-600 text-white"
                        />
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-zinc-400">Horas/día:</span>
                        <Input
                          type="number"
                          value={config.horas_dia}
                          onChange={(e) => actualizarBomba(presa, 'horas_dia', e.target.value)}
                          className="w-20 h-7 text-center bg-zinc-900 border-zinc-600 text-white"
                        />
                      </div>
                      <div className="pt-2 border-t border-zinc-700 text-center">
                        <span className="text-xs text-zinc-500">Potencia total: </span>
                        <span className="text-sm font-bold text-green-400">{config.cantidad * config.potencia_cv} CV</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <p className="text-[10px] text-zinc-500 mt-3 text-center">
                Los cambios se guardan automáticamente. Total: {stats.potencia_total_cv} CV ({stats.potencia_total_kw.toFixed(1)} kW)
              </p>
            </Card>
          )}

          {/* Summary Cards */}
          <div className="grid grid-cols-6 gap-2 mb-4">
            <Card className="bg-blue-900/20 border-blue-800/30 p-2">
              <div className="flex items-center gap-1 mb-1">
                <Droplets className="w-3 h-3 text-blue-400" />
                <span className="text-[9px] text-zinc-400 uppercase">Riego Anual</span>
              </div>
              <p className="text-xl font-bold text-blue-300 font-mono">{totalRiegoAnualHm3} Hm³</p>
            </Card>
            
            <Card className="bg-green-900/20 border-green-800/30 p-2">
              <div className="flex items-center gap-1 mb-1">
                <Zap className="w-3 h-3 text-green-400" />
                <span className="text-[9px] text-zinc-400 uppercase">Ahorro Energía</span>
              </div>
              <p className="text-xl font-bold text-green-300 font-mono">{stats.ahorro_porcentaje}%</p>
            </Card>
            
            <Card className="bg-teal-900/20 border-teal-800/30 p-2">
              <div className="flex items-center gap-1 mb-1">
                <Leaf className="w-3 h-3 text-teal-400" />
                <span className="text-[9px] text-zinc-400 uppercase">CO₂ Evitado</span>
              </div>
              <p className="text-xl font-bold text-teal-300 font-mono">{formatNumber(stats.co2_evitado)} kg</p>
            </Card>
            
            <Card className="bg-emerald-900/20 border-emerald-800/30 p-2">
              <div className="flex items-center gap-1 mb-1">
                <Leaf className="w-3 h-3 text-emerald-400" />
                <span className="text-[9px] text-zinc-400 uppercase">Árboles Equiv.</span>
              </div>
              <p className="text-xl font-bold text-emerald-300 font-mono">{formatNumber(stats.arboles_equivalentes)}</p>
            </Card>

            <Card className="bg-amber-900/20 border-amber-800/30 p-2">
              <div className="flex items-center gap-1 mb-1">
                <Factory className="w-3 h-3 text-amber-400" />
                <span className="text-[9px] text-zinc-400 uppercase">Total Bombas</span>
              </div>
              <p className="text-xl font-bold text-amber-300 font-mono">{stats.potencia_total_cv} CV</p>
            </Card>

            <Card className="bg-yellow-900/20 border-yellow-800/30 p-2">
              <div className="flex items-center gap-1 mb-1">
                <Sun className="w-3 h-3 text-yellow-400" />
                <span className="text-[9px] text-zinc-400 uppercase">Ahorro €</span>
              </div>
              <p className="text-xl font-bold text-yellow-300 font-mono">{formatNumber(stats.coste_evitado)} €</p>
            </Card>
          </div>

          {/* GRÁFICAS PRINCIPALES */}
          <div className="grid grid-cols-2 gap-4 mb-4">
            
            {/* GRÁFICA 1: RIEGO Y AHORRO ENERGÉTICO */}
            <Card className="bg-zinc-900/50 border-green-800/30 p-4">
              <h3 className="text-sm font-bold text-green-300 uppercase mb-2 flex items-center gap-2">
                <TrendingUp className="w-4 h-4" />
                RIEGO Y AHORRO ENERGÉTICO MENSUAL
              </h3>
              <ResponsiveContainer width="100%" height={220}>
                <ComposedChart data={datosAhorroMensual}>
                  <defs>
                    <linearGradient id="colorRiego" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.8}/>
                      <stop offset="95%" stopColor="#3b82f6" stopOpacity={0.1}/>
                    </linearGradient>
                    <linearGradient id="colorAhorro" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#22c55e" stopOpacity={0.8}/>
                      <stop offset="95%" stopColor="#22c55e" stopOpacity={0.1}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                  <XAxis dataKey="mes" tick={{ fill: '#888', fontSize: 10 }} />
                  <YAxis yAxisId="left" tick={{ fill: '#888', fontSize: 10 }} label={{ value: 'Miles m³', angle: -90, position: 'insideLeft', fill: '#3b82f6', fontSize: 10 }} />
                  <YAxis yAxisId="right" orientation="right" tick={{ fill: '#888', fontSize: 10 }} label={{ value: 'MWh', angle: 90, position: 'insideRight', fill: '#22c55e', fontSize: 10 }} />
                  <Tooltip 
                    contentStyle={{ backgroundColor: '#1a1a2e', border: '1px solid #22c55e', borderRadius: '8px' }}
                    formatter={(value, name) => {
                      const labels = { riego_miles: 'Riego (miles m³)', ahorro_mwh: 'Ahorro (MWh)' };
                      return [value.toLocaleString('es-ES'), labels[name] || name];
                    }}
                  />
                  <Legend />
                  <Bar yAxisId="left" dataKey="riego_miles" fill="url(#colorRiego)" radius={[4, 4, 0, 0]} name="Riego (miles m³)" />
                  <Line yAxisId="right" type="monotone" dataKey="ahorro_mwh" stroke="#22c55e" strokeWidth={3} dot={{ fill: '#22c55e', r: 4 }} name="Ahorro (MWh)" />
                </ComposedChart>
              </ResponsiveContainer>
              <div className="mt-2 p-2 bg-green-900/30 rounded border border-green-700/50 text-center">
                <span className="text-green-400 font-bold text-lg">{formatNumber(stats.ahorro_kwh)} kWh/año ahorrados</span>
                <span className="text-zinc-500 text-sm ml-2">({stats.ahorro_porcentaje}% menos)</span>
              </div>
            </Card>

            {/* GRÁFICA 2: CO2 EVITADO */}
            <Card className="bg-zinc-900/50 border-teal-800/30 p-4">
              <h3 className="text-sm font-bold text-teal-300 uppercase mb-2 flex items-center gap-2">
                <Leaf className="w-4 h-4" />
                CO₂ EVITADO Y ÁRBOLES EQUIVALENTES
              </h3>
              <ResponsiveContainer width="100%" height={220}>
                <ComposedChart data={datosCO2}>
                  <defs>
                    <linearGradient id="colorCO2" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#14b8a6" stopOpacity={0.8}/>
                      <stop offset="95%" stopColor="#14b8a6" stopOpacity={0.1}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                  <XAxis dataKey="mes" tick={{ fill: '#888', fontSize: 10 }} />
                  <YAxis yAxisId="left" tick={{ fill: '#888', fontSize: 10 }} label={{ value: 'kg CO₂', angle: -90, position: 'insideLeft', fill: '#14b8a6', fontSize: 10 }} />
                  <YAxis yAxisId="right" orientation="right" tick={{ fill: '#888', fontSize: 10 }} label={{ value: 'árboles', angle: 90, position: 'insideRight', fill: '#10b981', fontSize: 10 }} />
                  <Tooltip 
                    contentStyle={{ backgroundColor: '#1a1a2e', border: '1px solid #14b8a6', borderRadius: '8px' }}
                    formatter={(value, name) => {
                      if (name === 'co2_evitado') return [`${formatNumber(value)} kg`, 'CO₂ Evitado'];
                      return [`${value}`, 'Árboles equiv.'];
                    }}
                  />
                  <Legend />
                  <Bar yAxisId="left" dataKey="co2_evitado" fill="url(#colorCO2)" radius={[4, 4, 0, 0]} name="CO₂ Evitado (kg)" />
                  <Line yAxisId="right" type="monotone" dataKey="arboles" stroke="#10b981" strokeWidth={3} dot={{ fill: '#10b981', r: 4 }} name="Árboles equiv." />
                </ComposedChart>
              </ResponsiveContainer>
              <div className="mt-2 p-2 bg-teal-900/30 rounded border border-teal-700/50 text-center">
                <span className="text-teal-400 font-bold text-lg">{formatNumber(stats.co2_evitado)} kg CO₂/año</span>
                <span className="text-zinc-500 text-sm ml-2">≈ {formatNumber(stats.arboles_equivalentes)} árboles</span>
              </div>
            </Card>
          </div>

          {/* Fila inferior */}
          <div className="grid grid-cols-3 gap-4">
            
            {/* Distribución del sistema */}
            <Card className="bg-zinc-900/50 border-zinc-800/50 p-4">
              <h3 className="text-sm font-bold text-zinc-300 uppercase mb-2">Distribución Energética</h3>
              <ResponsiveContainer width="100%" height={150}>
                <PieChart>
                  <Pie
                    data={[
                      { name: 'Gravedad', value: parseFloat(stats.ahorro_porcentaje), color: '#22c55e' },
                      { name: 'Bombeo', value: 100 - parseFloat(stats.ahorro_porcentaje), color: '#3b82f6' }
                    ]}
                    cx="50%"
                    cy="50%"
                    innerRadius={40}
                    outerRadius={65}
                    paddingAngle={3}
                    dataKey="value"
                  >
                    <Cell fill="#22c55e" />
                    <Cell fill="#3b82f6" />
                  </Pie>
                  <Tooltip formatter={(value) => [`${value.toFixed(1)}%`]} />
                </PieChart>
              </ResponsiveContainer>
              <div className="flex justify-center gap-4 text-xs">
                <span className="flex items-center gap-1"><span className="w-3 h-3 bg-green-500 rounded"></span> Gravedad {stats.ahorro_porcentaje}%</span>
                <span className="flex items-center gap-1"><span className="w-3 h-3 bg-blue-500 rounded"></span> Bombeo {(100 - parseFloat(stats.ahorro_porcentaje)).toFixed(1)}%</span>
              </div>
            </Card>

            {/* Aportaciones Verdes */}
            <Card className="bg-zinc-900/50 border-zinc-800/50 p-4">
              <h3 className="text-sm font-bold text-green-300 uppercase mb-2 flex items-center gap-2">
                <Leaf className="w-4 h-4" />
                Aportaciones Verdes Anuales
              </h3>
              <div className="space-y-2">
                <div className="flex items-center justify-between p-2 rounded bg-blue-900/20 border-l-4 border-blue-500">
                  <span className="text-xs text-zinc-400">Agua Regada</span>
                  <span className="text-sm font-bold font-mono text-blue-400">{totalRiegoAnualHm3} Hm³</span>
                </div>
                <div className="flex items-center justify-between p-2 rounded bg-green-900/20 border-l-4 border-green-500">
                  <span className="text-xs text-zinc-400">Energía Ahorrada</span>
                  <span className="text-sm font-bold font-mono text-green-400">{formatNumber(stats.ahorro_kwh)} kWh</span>
                </div>
                <div className="flex items-center justify-between p-2 rounded bg-teal-900/20 border-l-4 border-teal-500">
                  <span className="text-xs text-zinc-400">CO₂ Evitado</span>
                  <span className="text-sm font-bold font-mono text-teal-400">{formatNumber(stats.co2_evitado)} kg</span>
                </div>
                <div className="flex items-center justify-between p-2 rounded bg-yellow-900/20 border-l-4 border-yellow-500">
                  <span className="text-xs text-zinc-400">Coste Evitado</span>
                  <span className="text-sm font-bold font-mono text-yellow-400">{formatNumber(stats.coste_evitado)} €</span>
                </div>
              </div>
            </Card>

            {/* Resumen Bombas por Presa */}
            <Card className="bg-zinc-900/50 border-zinc-800/50 p-4">
              <h3 className="text-sm font-bold text-amber-300 uppercase mb-2 flex items-center gap-2">
                <Factory className="w-4 h-4" />
                Bombas por Presa
              </h3>
              <div className="space-y-2">
                {Object.entries(bombas).map(([presa, config]) => (
                  <div key={presa} className="flex items-center justify-between p-2 rounded bg-zinc-800/50">
                    <div>
                      <span className="text-xs font-bold text-white">{presa}</span>
                      <span className="text-[10px] text-zinc-500 ml-2">{config.horas_dia}h/día</span>
                    </div>
                    <div className="text-right">
                      <span className="text-sm font-bold text-amber-400">{config.cantidad} × {config.potencia_cv} CV</span>
                      <span className="text-[10px] text-zinc-500 block">{(config.cantidad * config.potencia_cv * 0.7355).toFixed(1)} kW</span>
                    </div>
                  </div>
                ))}
                <div className="pt-2 border-t border-zinc-700 flex justify-between">
                  <span className="text-xs text-zinc-400">TOTAL:</span>
                  <span className="text-sm font-bold text-amber-300">{stats.potencia_total_cv} CV ({stats.potencia_total_kw.toFixed(1)} kW)</span>
                </div>
              </div>
            </Card>
          </div>

          {/* Footer explicativo */}
          <div className="mt-4 p-3 bg-green-900/10 border border-green-800/30 rounded-lg">
            <p className="text-xs text-zinc-400">
              <strong className="text-green-400">Sistema de Riego por Gravedad:</strong> Las presas de Parralillo, Siberio y Caidero aprovechan la topografía natural para distribuir {totalRiegoAnualHm3} Hm³ anuales a {RIEGO_BASE.hectareas_regables} hectáreas de cultivo.
              Las bombas solo se usan para elevar agua a zonas altas, logrando un <strong className="text-green-300">{stats.ahorro_porcentaje}% de ahorro energético</strong>.
              <span className="text-blue-400 ml-2">Pulsa "Riego Mensual" para introducir los datos reales de cada mes.</span>
            </p>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

// Botón para abrir desde el header (estilo similar a Gestión de Vaciado)
export const IrrigationStatsButton = ({ onClick }) => {
  return (
    <Button 
      variant="outline" 
      size="sm"
      onClick={onClick}
      className="bg-green-600/20 border-green-500/30 text-green-400 hover:bg-green-600/30 hover:text-green-300 h-8 px-3 text-xs"
      data-testid="irrigation-stats-header-btn"
    >
      <Leaf className="w-4 h-4 mr-1.5" />
      Estadísticas Riego
    </Button>
  );
};

// Widget compacto para el dashboard móvil
export const IrrigationWidget = ({ onClick }) => {
  const [bombas] = useState(() => {
    const saved = localStorage.getItem('centinela_bombas');
    return saved ? JSON.parse(saved) : DEFAULT_BOMBAS;
  });
  
  const [riegoMensual] = useState(() => {
    const saved = localStorage.getItem('centinela_riego_mensual');
    return saved ? JSON.parse(saved) : DEFAULT_RIEGO_MENSUAL;
  });

  const potencia_total = Object.values(bombas).reduce((sum, b) => sum + (b.cantidad * b.potencia_cv), 0);
  const totalRiego = Object.values(riegoMensual).reduce((sum, val) => sum + val, 0);

  return (
    <div 
      onClick={onClick}
      className="bg-gradient-to-r from-green-900/30 to-teal-900/20 rounded-lg px-3 py-2 cursor-pointer hover:from-green-800/40 hover:to-teal-800/30 transition-colors border border-green-800/30"
    >
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2">
          <Droplets className="w-4 h-4 text-green-400" />
          <span className="text-xs uppercase tracking-wider text-green-300/80 font-semibold">Riego La Aldea</span>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-2 text-center">
        <div>
          <p className="text-lg font-bold text-green-300 font-mono">{(totalRiego / 1000000).toFixed(1)}</p>
          <p className="text-[9px] text-zinc-500">Hm³/año</p>
        </div>
        <div>
          <p className="text-lg font-bold text-teal-300 font-mono">95%</p>
          <p className="text-[9px] text-zinc-500">Ahorro</p>
        </div>
        <div>
          <p className="text-lg font-bold text-yellow-300 font-mono">{potencia_total}</p>
          <p className="text-[9px] text-zinc-500">CV Total</p>
        </div>
      </div>
    </div>
  );
};

export default IrrigationStats;
